<template>
  <q-expansion-item
    v-for="(item, idx) in menu"
    :key="item.menu_id"
    :header-inset-level="deepLevel / 5"
    :class="['item', `item--level${Math.min(deepLevel, 2)}`, { item__active: PAGES[item.menu_action] === path }]"
    header-class="item__header"
    :label="item.menu_name"
    :model-value="expanded(item)"
    :to="getHref(item.menu_action)"
    :hide-expand-icon="!item.sub_menus?.length"
    expand-icon="fa-solid fa-chevron-right"
    expand-icon-class="item__exp_icon"
    @before-show="this.opened[item.menu_id] = true"
  >
    <template #header="props">
      <div
        class="q-item__section q-item__section--main"
        style="display: flex; align-items: center; gap: 7px;"
      >
        <span
          v-if="deepLevel === 0"
          class="item__icon"
          :style="{ background: this.iconColor(item, idx) }"
        >
          <q-icon
            :name="`svguse:/img/icons/slice.svg#${menuIcons[item.icon.toLowerCase()] ? item.icon.toLowerCase() || 'document' : 'document'}|${menuIcons[item.icon.toLowerCase()] ? menuIcons[item.icon.toLowerCase()].viewBox : '0 0 18 17'}`"
            size="32px"
            color="#fff"
            :class="'text-primary'"
            viewport="0 0 10 17"
          />
        </span>
        <div
          class="q-item__section column q-item__section--main justify-center"
        >
          {{ item.menu_name }}
        </div>
      </div>
    </template>

    <!-- подпункты создаются, только когда раздел раскрывают (или он открыт сам — активная ветка, поиск):
         раньше в DOM висели все ~200 свёрнутых пунктов -->
    <menu-list
      v-if="item.sub_menus && (this.opened[item.menu_id] || this.expanded(item))"
      :menu="item.sub_menus"
      :deep-level="deepLevel + 1"
    />
  </q-expansion-item>
</template>

<script>
import { PAGES } from '@/constants'
import { menuIcons } from './services'

const SECTION_COLORS = ['#0060FE', '#16A34A', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#F97316', '#14B8A6', '#6366F1', '#EF4444']

export default {
  name: 'menu-list',
  props: {
    menu: {
      type: Array,
      required: true
    },
    deepLevel: {
      type: Number,
      default: 0
    },
    expandAll: { type: Boolean, default: false }
  },
  data() {
    return {PAGES, menuIcons, opened: {}}
  },
  computed: {
    path () {
      return this.$route?.path ? this.$route.path : '/'
    },
  },
  methods: {
    // цвет квадрата-иконки раздела (как в настройках macOS): по порядку разделов из палитры
    iconColor (item, idx) {
      return SECTION_COLORS[idx % SECTION_COLORS.length]
    },
    getHref (menu_action) {
      if (!menu_action || !PAGES[menu_action] || PAGES[menu_action]?.includes(':')) return
      return PAGES[menu_action]
    },
    expanded (item) {
      if (this.expandAll && item.sub_menus?.length) return true
      return this.isActiveBranch(item)
    },
    isActiveBranch (item) {
      if (!item) return false
      const selfActive = this.PAGES[item.menu_action] === this.path
      const kids = item.sub_menus || []
      const childActive = kids.some(k => this.isActiveBranch(k))
      return selfActive || childActive
    }
  }
}
</script>

<style lang="scss">
// Пункты меню: скруглённые «таблетки» на светлой голубоватой панели; активный — белая плашка с синим текстом
// и иконкой; подпункты раздела собраны тонкой линией-деревом слева. Цвета — --ms-nav-* в theme.scss.
.item {
  color: var(--ms-nav-text);
  padding: 0;
  border: none;
  margin: 1px 0;

  &__header {
    min-height: 38px !important;
    padding: 6px 10px 6px 12px;
    padding-left: 12px !important; // перебиваем инлайн-отступ header-inset-level — отступ задаёт линия-дерево
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 500;
    letter-spacing: -0.005em;
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;

    // своя подсветка вместо серой «вспышки» Quasar
    .q-focus-helper {
      display: none;
    }

    &:hover {
      background: var(--ms-nav-hover);
      color: var(--ms-primary);

    }
  }

  // иконки разделов — белые значки в цветных квадратах (цвет — iconColor); синий зашит в спрайт
  // public/img/icons/slice.svg (#0060FE), поэтому сам значок перекрашиваем в белый фильтром
  &__icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin-right: 3px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    .q-icon.q-icon {
      width: 16px !important;
      height: 16px !important;
      font-size: 16px !important;
      filter: brightness(0) invert(1);
    }
  }

  // подпункты: мельче и спокойнее
  &--level1 > .q-expansion-item__container > .item__header,
  &--level2 > .q-expansion-item__container > .item__header {
    min-height: 32px !important;
    padding: 4px 10px;
    padding-left: 10px !important;
    font-size: 13px;
    color: var(--ms-nav-text-sub);
  }

  // раскрытый раздел: подпункты сдвинуты и собраны линией, выходящей из-под иконки раздела
  &--level0 > .q-expansion-item__container > .q-expansion-item__content,
  &--level1 > .q-expansion-item__container > .q-expansion-item__content {
    margin: 2px 0 6px 20px;
    padding-left: 8px;
    border-left: 1.5px solid var(--ms-nav-tree);
  }

  // заголовок раскрытого раздела — ярче, как «папка»
  &.q-expansion-item--expanded > .q-expansion-item__container > .item__header {
    color: var(--ms-nav-text-strong);
    font-weight: 600;
  }

  .q-icon {
    font-size: 18px !important;
  }

  &__link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    padding: 8px 16px;
    width: 100%;

    &_icon { min-width: 40px; }
    &_dot { font-size: 8px !important; }
  }

  &__exp {
    &_icon {
      font-size: 20px !important;
      color: var(--ms-nav-icon);
      .q-icon {
        font-size: 11px !important;
      }
    }
  }

  // стрелка раздела: свёрнут — вправо, раскрыт — вниз (Quasar по умолчанию поворачивает на 180°)
  .q-expansion-item__toggle-icon {
    transition: transform 0.2s;
  }

  .q-expansion-item__toggle-icon--rotated {
    transform: rotate(90deg);
  }

  // активный пункт — белая плашка с тонкой рамкой и мягкой тенью, текст синий (без сплошной заливки)
  &__active > .q-expansion-item__container > .item__header {
    background: var(--ms-surface);
    box-shadow: inset 0 0 0 1px var(--ms-nav-border), var(--ms-nav-active-shadow);
    color: var(--ms-primary) !important;
    font-weight: 600;

    div {
      color: var(--ms-primary);
    }

    .item__exp_icon,
    .q-expansion-item__toggle-icon {
      color: var(--ms-primary) !important;
    }
  }
}
</style>
